'use client';

import { FC, Key } from 'react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Image,
} from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useStore } from '@/store/zustand/zustand.setup';
import { cn } from '@/utils';

type StudioPreviewLanguageSelectorProps = {
  presentation?: 'header' | 'mobileMenu';
};

const FLAG_CDN = (code: string) =>
  `https://flagcdn.com/${code.toLowerCase() === 'en' ? 'gb' : code.toLowerCase()}.svg`;

/** Language selector for studio preview — persists locale via the shared locale cookie. */
export const StudioPreviewLanguageSelector: FC<StudioPreviewLanguageSelectorProps> = ({
  presentation = 'header',
}) => {
  const coreConfig = useCoreConfig();
  const previewLocale = useStore(state => state.studio.previewLocale ?? 'ro');
  const setPreviewLocale = useStore(state => state.setPreviewLocale);
  const headerContent = useHeaderContent();
  const resolveThemeColor = useResolveThemeColor();

  const currentLanguage = previewLocale.toLowerCase();
  const isMobileMenu = presentation === 'mobileMenu';
  const flagWidth = isMobileMenu ? 28 : 20;
  const flagHeight = isMobileMenu ? 21 : 15;
  const labelColor = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');

  const handleChange = (key: Key) => {
    const newLocale = key.toString();
    if (newLocale) {
      setPreviewLocale(newLocale);
    }
  };

  if (coreConfig.supportedLanguages.length <= 1) {
    return null;
  }

  return (
    <Dropdown className="dropdown-custom" classNames={{ content: 'min-w-0' }}>
      <DropdownTrigger>
        <Button
          variant="light"
          className={cn('min-w-0 text-inherit', isMobileMenu ? 'px-2 py-1.5' : 'p-2')}
        >
          <div className={cn('flex items-center gap-2', isMobileMenu && 'text-base')}>
            <Image
              alt={`${currentLanguage} flag`}
              src={FLAG_CDN(currentLanguage)}
              width={flagWidth}
              height={flagHeight}
              style={{ borderRadius: 0 }}
            />
            {!isMobileMenu ? (
              <span style={{ color: labelColor }}>{currentLanguage.toUpperCase()}</span>
            ) : null}
          </div>
        </Button>
      </DropdownTrigger>

      <DropdownMenu
        aria-label="Language Selector"
        className="dropdown-menu-custom"
        onAction={handleChange}
      >
        {coreConfig.supportedLanguages.map(language => (
          <DropdownItem
            key={language.code}
            value={language.code}
            textValue={language.label}
            className={cn(currentLanguage === language.code ? 'bg-gray-200' : 'bg-transparent')}
          >
            <div
              className={cn(
                'flex items-center justify-start gap-2 px-2',
                isMobileMenu && 'py-0.5 text-base',
              )}
            >
              <Image
                alt={`${language.code} flag`}
                src={FLAG_CDN(language.code)}
                width={flagWidth}
                height={flagHeight}
                style={{ borderRadius: 0 }}
              />
              {language.label}
            </div>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

export default StudioPreviewLanguageSelector;
